
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  GraduationCap, Code, Award, Layout, 
  BookOpen, Users, File, MessageSquare,
  Check, ChevronRight, Lightbulb, Shield,
  ArrowRight, Star, Monitor, BookMarked
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from './Header';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const Index = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: <BookOpen className="h-8 w-8 text-bytesplash-primary" />,
      title: "Interactive Courses",
      description: "Access a wide range of programming courses with interactive lessons and hands-on exercises.",
      link: "/courses"
    },
    {
      icon: <Award className="h-8 w-8 text-purple-500" />,
      title: "Skill Assessments",
      description: "Test your knowledge with our comprehensive skill assessments and get detailed feedback.",
      link: "/assessments"
    },
    {
      icon: <Code className="h-8 w-8 text-emerald-500" />,
      title: "Code Practice",
      description: "Sharpen your coding skills with our interactive code practice platform.",
      link: "/code-practice"
    },
    {
      icon: <MessageSquare className="h-8 w-8 text-yellow-500" />,
      title: "Interview Prep",
      description: "Prepare for technical interviews with mock questions and scenarios.",
      link: "/interview-prep"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Front-end Developer",
      company: "TechCorp",
      content: "This platform helped me transition from a beginner to a confident developer. The assessments were particularly helpful in identifying my weak areas."
    },
    {
      name: "David Chen",
      role: "Full Stack Engineer",
      company: "StartupInc",
      content: "The interview prep section was crucial for my job search. I aced my technical interviews thanks to the practice I got here."
    },
    {
      name: "Maria Rodriguez",
      role: "Computer Science Student",
      content: "As a student, the structured learning path and hands-on coding exercises have been invaluable supplements to my university education."
    }
  ];

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <Header />
      <section className="relative bg-gradient-to-r from-bytesplash-primary to-bytesplash-dark text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLThoLTJ2LTRoMnY0em0wLThoLTJWOGgydjEweiIvPjxwYXRoIGQ9Ik00NCAxOGgtNHYtMmg0djJ6bTgtNGgtNHYtMmg0djJ6TTQ3IDI4aC0zdi0yaDN2MnoiLz48cGF0aCBkPSJNMTIgMzRIMHYtMmgxMnYyek0xMiAyNEgwdi0yaDEydjJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="relative max-w-7xl mx-auto px-4 sm:px-6 py-28 md:py-36 flex flex-col items-center"
        >
          <span className="px-4 py-1.5 bg-white/10 text-white text-sm font-medium rounded-full mb-8 backdrop-blur-sm border border-white/20">
            The Ultimate Programming Learning Platform
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-center max-w-4xl mb-6 [text-shadow:_0_1px_5px_rgb(0_0_0_/_20%)]">
            Master Programming Skills with Interactive Learning
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 text-center max-w-3xl mb-10">
            Take your coding skills to the next level with our comprehensive learning platform - courses, assessments, practice, and interview prep all in one place.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button size="lg" asChild className="px-8 py-6 text-lg bg-white hover:bg-white/90 text-bytesplash-primary transition-all duration-300 shadow-lg hover:shadow-xl">
              <Link to="/courses" className="flex items-center gap-2">
                Explore Courses <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="bg-white/10 hover:bg-white/20 text-white border-white/20 px-8 py-6 text-lg transition-all duration-300">
              <Link to="/assessments" className="flex items-center gap-2">
                Try Assessments <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-bytesplash-primary/50 to-transparent"></div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center mb-16"
          >
            <span className="inline-block px-3 py-1 bg-bytesplash-light text-bytesplash-primary text-sm font-medium rounded-full mb-3">
              Features
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our platform provides all the tools and resources you need to become a proficient developer.
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={fadeIn}>
                <Card className="border border-gray-200 hover:shadow-lg transition-all duration-300 h-full flex flex-col hover:-translate-y-1">
                  <CardHeader>
                    <div className="w-16 h-16 rounded-2xl bg-bytesplash-light flex items-center justify-center mb-4">
                      {feature.icon}
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <CardDescription className="text-gray-600 text-base">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                  <CardFooter>
                    <Button variant="outline" asChild className="w-full group hover:bg-[#0071bd]">
                      <Link to={feature.link} className="flex justify-between items-center">
                        <span>Explore</span>
                        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-bytesplash-light to-white/50 opacity-50 z-0"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center mb-16"
          >
            <span className="inline-block px-3 py-1 bg-bytesplash-primary/10 text-bytesplash-primary text-sm font-medium rounded-full mb-3">
              Process
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Our Platform Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A step-by-step approach to mastering programming skills
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div variants={fadeIn} className="relative">
              <div className="text-center p-8 bg-white rounded-xl shadow-soft border border-gray-100 h-full">
                <div className="w-16 h-16 rounded-full bg-bytesplash-light flex items-center justify-center mx-auto mb-6">
                  <GraduationCap className="h-8 w-8 text-bytesplash-primary" />
                </div>
                <div className="absolute -right-4 top-1/2 hidden md:block text-bytesplash-primary/30">
                  <ArrowRight className="h-8 w-8" />
                </div>
                <span className="block text-sm font-semibold text-bytesplash-primary mb-2">Step 1</span>
                <h3 className="text-xl font-semibold mb-3">Learn</h3>
                <p className="text-gray-600">
                  Start with our structured courses covering programming fundamentals to advanced topics
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeIn} className="relative">
              <div className="text-center p-8 bg-white rounded-xl shadow-soft border border-gray-100 h-full">
                <div className="w-16 h-16 rounded-full bg-bytesplash-light flex items-center justify-center mx-auto mb-6">
                  <Code className="h-8 w-8 text-bytesplash-primary" />
                </div>
                <div className="absolute -right-4 top-1/2 hidden md:block text-bytesplash-primary/30">
                  <ArrowRight className="h-8 w-8" />
                </div>
                <span className="block text-sm font-semibold text-bytesplash-primary mb-2">Step 2</span>
                <h3 className="text-xl font-semibold mb-3">Practice</h3>
                <p className="text-gray-600">
                  Apply your knowledge through interactive coding exercises and real-world projects
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeIn} className="relative">
              <div className="text-center p-8 bg-white rounded-xl shadow-soft border border-gray-100 h-full">
                <div className="w-16 h-16 rounded-full bg-bytesplash-light flex items-center justify-center mx-auto mb-6">
                  <Award className="h-8 w-8 text-bytesplash-primary" />
                </div>
                <span className="block text-sm font-semibold text-bytesplash-primary mb-2">Step 3</span>
                <h3 className="text-xl font-semibold mb-3">Master</h3>
                <p className="text-gray-600">
                  Test your skills with assessments and prepare for technical interviews
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Course Categories */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-40 bg-gradient-to-b from-white to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center mb-16"
          >
            <span className="inline-block px-3 py-1 bg-bytesplash-light text-bytesplash-primary text-sm font-medium rounded-full mb-3">
              Curriculum
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Explore Our Course Categories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From web development to data science, we have courses for every interest
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
          >
            <Tabs defaultValue="web" className="max-w-4xl mx-auto">
              <TabsList className="grid grid-cols-3 mb-8">
                <TabsTrigger value="web">Web Development</TabsTrigger>
                <TabsTrigger value="mobile">Mobile Development</TabsTrigger>
                <TabsTrigger value="data">Data Science</TabsTrigger>
              </TabsList>
              
              <TabsContent value="web" className="rounded-lg p-8 bg-white shadow-soft">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">HTML & CSS Fundamentals</h4>
                      <p className="text-gray-600">Master the building blocks of web development</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">JavaScript Mastery</h4>
                      <p className="text-gray-600">From basics to advanced concepts</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">React Development</h4>
                      <p className="text-gray-600">Build modern UI with React</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">Full Stack Development</h4>
                      <p className="text-gray-600">Connect frontend with backend technologies</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <Button asChild className="px-6 py-5 rounded-xl bg-bytesplash-primary hover:bg-bytesplash-dark text-lg">
                    <Link to="/courses" className="flex items-center gap-2">
                      View All Web Courses <Monitor className="h-5 w-5" />
                    </Link>
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="mobile" className="rounded-lg p-8 bg-white shadow-soft">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">React Native</h4>
                      <p className="text-gray-600">Build cross-platform mobile apps</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">iOS Development with Swift</h4>
                      <p className="text-gray-600">Create native iOS applications</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">Android Development</h4>
                      <p className="text-gray-600">Build for the Android ecosystem</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">Flutter Development</h4>
                      <p className="text-gray-600">Google's UI toolkit for mobile apps</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <Button asChild className="px-6 py-5 rounded-xl bg-bytesplash-primary hover:bg-bytesplash-dark text-lg">
                    <Link to="/courses" className="flex items-center gap-2">
                      View All Mobile Courses <Monitor className="h-5 w-5" />
                    </Link>
                  </Button>
                </div>
              </TabsContent>
              
              <TabsContent value="data" className="rounded-lg p-8 bg-white shadow-soft">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">Python for Data Science</h4>
                      <p className="text-gray-600">Essential Python skills for data analysis</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">Machine Learning Fundamentals</h4>
                      <p className="text-gray-600">Introduction to ML algorithms</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">Data Visualization</h4>
                      <p className="text-gray-600">Create compelling visual representations</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1 mt-1">
                      <Check className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">Big Data Processing</h4>
                      <p className="text-gray-600">Work with large-scale data sets</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 text-center">
                  <Button asChild className="px-6 py-5 rounded-xl bg-bytesplash-primary hover:bg-bytesplash-dark text-lg">
                    <Link to="/courses" className="flex items-center gap-2">
                      View All Data Science Courses <Monitor className="h-5 w-5" />
                    </Link>
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-bytesplash-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLThoLTJ2LTRoMnY0em0wLThoLTJWOGgydjEweiIvPjxwYXRoIGQ9Ik00NCAxOGgtNHYtMmg0djJ6bTgtNGgtNHYtMmg0djJ6TTQ3IDI4aC0zdi0yaDN2MnoiLz48cGF0aCBkPSJNMTIgMzRIMHYtMmgxMnYyek0xMiAyNEgwdi0yaDEydjJ6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30"></div>
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <motion.div variants={fadeIn} className="p-6 rounded-xl backdrop-blur-sm bg-white/5 border border-white/10">
              <div className="text-4xl font-bold mb-2">50+</div>
              <div className="text-blue-100">Courses</div>
            </motion.div>
            <motion.div variants={fadeIn} className="p-6 rounded-xl backdrop-blur-sm bg-white/5 border border-white/10">
              <div className="text-4xl font-bold mb-2">10k+</div>
              <div className="text-blue-100">Students</div>
            </motion.div>
            <motion.div variants={fadeIn} className="p-6 rounded-xl backdrop-blur-sm bg-white/5 border border-white/10">
              <div className="text-4xl font-bold mb-2">200+</div>
              <div className="text-blue-100">Assessments</div>
            </motion.div>
            <motion.div variants={fadeIn} className="p-6 rounded-xl backdrop-blur-sm bg-white/5 border border-white/10">
              <div className="text-4xl font-bold mb-2">95%</div>
              <div className="text-blue-100">Success Rate</div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center mb-16"
          >
            <span className="inline-block px-3 py-1 bg-bytesplash-light text-bytesplash-primary text-sm font-medium rounded-full mb-3">
              Testimonials
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Students Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from those who have achieved their goals with our platform
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {testimonials.map((testimonial, index) => (
              <motion.div key={index} variants={fadeIn}>
                <Card className="border border-gray-200 bg-white h-full flex flex-col shadow-soft">
                  <CardContent className="pt-6 flex-grow">
                    <div className="mb-4 flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 text-lg">"<span className="italic font-light">{testimonial.content}</span>"</p>
                    <div className="flex items-center mt-auto">
                      <div className="w-12 h-12 rounded-full bg-bytesplash-light/80 text-bytesplash-primary flex items-center justify-center font-semibold text-lg mr-4">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-lg">{testimonial.name}</p>
                        <p className="text-sm text-gray-600">{testimonial.role}{testimonial.company && `, ${testimonial.company}`}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-bytesplash-dark to-bytesplash-primary text-white">
        <motion.div 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeIn}
          className="max-w-7xl mx-auto px-4 sm:px-6 text-center"
        >
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 [text-shadow:_0_1px_5px_rgb(0_0_0_/_20%)]">Ready to Start Your Coding Journey?</h2>
            <p className="text-xl text-blue-100 mb-10">
              Join thousands of students who are already building their future with our comprehensive learning platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild className="px-8 py-6 text-lg bg-white hover:bg-white/90 text-bytesplash-primary">
                <Link to="/courses" className="flex items-center gap-2">
                  Browse Courses <BookMarked className="h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="text-white bg-gray-400 hover:bg-white/10 px-8 py-6 text-lg">
                <Link to="/login" className="flex items-center gap-2">
                  Login to your Account <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Key Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center mb-16"
          >
            <span className="inline-block px-3 py-1 bg-bytesplash-light text-bytesplash-primary text-sm font-medium rounded-full mb-3">
              Benefits
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Benefits that set us apart from other learning platforms
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div variants={fadeIn} className="text-center p-8 bg-white rounded-xl shadow-soft border border-gray-100">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <Lightbulb className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Learn by Doing</h3>
              <p className="text-gray-600">
                Our hands-on approach ensures you gain practical experience through interactive exercises and real-world projects.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="text-center p-8 bg-white rounded-xl shadow-soft border border-gray-100">
              <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Expert Mentors</h3>
              <p className="text-gray-600">
                Learn from industry professionals with years of real-world experience who guide you through complex concepts.
              </p>
            </motion.div>

            <motion.div variants={fadeIn} className="text-center p-8 bg-white rounded-xl shadow-soft border border-gray-100">
              <div className="w-16 h-16 rounded-full bg-bytesplash-light flex items-center justify-center mx-auto mb-6">
                <Shield className="h-8 w-8 text-bytesplash-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Recognized Certification</h3>
              <p className="text-gray-600">
                Earn certificates that are valued by employers and showcase your skills to potential recruiters.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Index;
